import React from "react";

const BulkImproveTagsModal = ({
  isOpen,
  onClose,
  originalTags,
  suggestedTags,
  onConfirm,
  isLoading,
}) => {
  if (!isOpen) return null;

  const handleTagUpdate = (index, field, value) => {
    const updatedTags = [...suggestedTags];
    updatedTags[index] = { ...updatedTags[index], [field]: value };
    onConfirm(updatedTags, false);
  };

  const LoadingState = () => (
    <div className="flex flex-col items-center justify-center py-12 space-y-4">
      <div className="w-16 h-16 relative">
        <div className="absolute inset-0 rounded-full border-t-2 border-primary animate-spin"></div>
        <div className="absolute inset-0 rounded-full border-2 border-gray-100"></div>
      </div>
      <div className="text-center space-y-2">
        <h3 className="text-lg font-medium text-gray-900">
          Deasy is improving your tags
        </h3>
        <p className="text-sm text-gray-500 max-w-sm">
          This may take a moment...
        </p>
      </div>
    </div>
  );

  const TagRow = ({ tag, index }) => {
    const originalTag = originalTags.find((t) => t.tag_id === tag.tag_id);
    const nameChanged = originalTag?.name !== tag.name;
    const descriptionChanged = originalTag?.description !== tag.description;
    const hasChanges = nameChanged || descriptionChanged;

    return (
      <tr key={tag.tag_id} className={hasChanges ? "bg-green-50/50" : ""}>
        <td className="px-4 py-6 w-1/5">
          <div
            className={`p-3 rounded-md text-left text-sm flex items-center gap-2`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-gray-500 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 7h.01M7 3h5a1.99 1.99 0 0 1 1.414.586l7 7a2 2 0 0 1 0 2.828l-7 7a2 2 0 0 1-2.828 0l-7-7A1.99 1.99 0 0 1 3 12V7a4 4 0 0 1 4-4z"
              />
            </svg>
            <span className="break-words">{tag.name || ""}</span>
          </div>
        </td>
        <td className="px-4 py-6 w-1/4">
          <div className="max-h-28 min-h-[80px] overflow-y-auto p-3 rounded-md text-sm text-left">
            {originalTag?.description || ""}
          </div>
        </td>
        <td className="px-4 py-6 w-2/4">
          <textarea
            value={tag.description || ""}
            onChange={(e) =>
              handleTagUpdate(index, "description", e.target.value)
            }
            rows="4"
            className={`w-full p-3 border border-gray-200 rounded-md min-h-[100px] resize-none text-sm`}
          />
        </td>
      </tr>
    );
  };

  const ModalHeader = () => (
    <div className="bg-primary/5 px-6 py-4 border-b border-primary/10">
      <div className="flex items-center gap-2 text-gray-800">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-primary"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
        <span className="text-lg font-medium">
          {isLoading ? "Improving Tags..." : "Review Improved Tags"}
        </span>
      </div>
    </div>
  );

  const ModalFooter = () => (
    <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse border-t border-gray-200">
      <button
        type="button"
        onClick={() => onConfirm(suggestedTags, true)}
        disabled={isLoading}
        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-5 py-2 bg-primary text-base font-medium text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-4 sm:w-auto sm:text-sm transition duration-150"
      >
        Apply All Improvements
      </button>
      <button
        type="button"
        onClick={onClose}
        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-5 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-4 sm:w-auto sm:text-sm transition duration-150"
      >
        Cancel
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 flex items-center justify-center">
      <div
        className="bg-white rounded-lg shadow-xl overflow-hidden w-full max-w-6xl transform transition-all mx-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
      >
        <ModalHeader />

        <div className="bg-white px-6 py-5 max-h-[70vh] overflow-y-auto">
          {isLoading ? (
            <LoadingState />
          ) : (
            <>
              <div className="mb-6">
                <p className="text-sm text-left text-gray-600">
                  Review the improved tags below. You can edit suggestions
                  before confirming.
                </p>
              </div>

              <div className="overflow-auto shadow-sm ring-1 ring-black ring-opacity-5 rounded-lg max-h-[60vh]">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 w-1/5">
                        Tag Name
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 w-1/4">
                        Original Description
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 w-2/4">
                        Improved Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {suggestedTags.map((tag, index) => (
                      <TagRow key={tag.tag_id} tag={tag} index={index} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <ModalFooter />
      </div>
    </div>
  );
};

export default BulkImproveTagsModal;
